import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  setConfigToColumns,
  setUmByFgUm,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnComboBox from "@shared/components/SnComboBox";
import SnLabel from "@shared/components/SnLabel";
import SnButton from "@shared/components/SnButton";
import SN_PUR_PTX_REG_001_POP_01 from "./SN_PUR_PTX_REG_001_POP_01";
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import { fnCheckAutoProcessV2 } from "../common/sn-pur-util";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

// 매입등록
const SN_PUR_PTX_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const [selectedValue, setSelectedValue] = useState<string>("001");
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        const configGrid1 = setConfigToColumns(rtn.grid.grid2, "MAS0002");
        const configGrid2 = setConfigToColumns(configGrid1, "PUR0105");
        setColums({
          grid1: rtn.grid.grid1,
          grid2: configGrid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_pur_ptx_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const modifiedRowsH =
        gridRefs.current["grid1"].current.getChange().insert;
      if (modifiedRowsH.length > 0) return false;

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        no_pur: "",
        dt_pur: today,
        cd_dept: userInfo?.cd_dept,
        cd_emp: searchInfo.cd_emp || userInfo?.cd_emp,
        nm_emp: searchInfo.nm_emp || userInfo?.nm_emp,
        yn_autoprocess: "N",
        cd_exch: "000",
        rt_exch: 1,
        fg_trns: searchInfo.fg_trns || 1,
        fg_vat: "21",
        cd_fin: "000",
        cd_cust: searchInfo.cd_cust || "",
        nm_cust_sort: searchInfo.nm_cust || "",
        cd_gp: searchInfo.cd_gp || userInfo.cd_gp || "",
        yn_docu: "N",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");

      const newRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_firm,
        no_line: maxNoLine + 1,
        no_pur: focusedNodeH.data?.no_pur,
        fg_trns: "1",
        fg_vat: "21",
        no_line_rcv: maxNoLine + 1,
        no_line_po: maxNoLine + 1,
      };
      return gridRefs.current["grid2"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return;
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      if (await fnCheckAutoProcessV2(gridRefs.current["grid1"])) return false;

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const purPthChanges = gridRefs.current["grid1"].current.getChange();
      const purPtlChanges = gridRefs.current["grid2"].current.getChange();

      const headerInserts = purPthChanges.insert ?? [];
      const lineInserts = purPtlChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return false;
      }

      if (
        purPthChanges.modifiedRowcount === 0 &&
        purPtlChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const cd_module: any = Menuprops.cd_module;
      const cd_menu: any = Menuprops.cd_menu;
      const noSlip = await applySlip({
        cd_column: "no_pur",
        cd_module: cd_module,
        no_line: 6,
        cd_prefix: "PTX",
        dt: focusedRowNodeH.data.dt_pur,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [purPthChanges, purPtlChanges],
        cd_menu: cd_menu,
      });

      const saveData = {
        pur_purchaseh: purPthChanges,
        pur_purchasel: purPtlChanges,
      };
      const ok = await fnSave("SN_PUR_PTX_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClck = async (id: string) => {
    if (id == "additem") {
      gridRefs.current["grid1"].current.stopEditing();
      const purPthChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (purPthChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz || focusedRowNodeH.data.cd_biz,
        cd_cust: focusedRowNodeH.data.cd_cust || "",
        nm_cust: focusedRowNodeH.data.nm_cust_sort || "",
        fg_trns: focusedRowNodeH.data.fg_trns,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
      };

      const component = (
        <SN_PUR_PTX_REG_001_POP_01 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "입고적용",
        children: component,
        validation: true,
      });
      if (!result || result === "close") return;

      let headerResult: any[] = result[0];
      let lineResult: any[] = result[1];

      if (focusedRowNodeH && headerResult[0]) {
        focusedRowNodeH.setDataValue(
          "cd_biz",
          headerResult[0].cd_biz,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "dt_req",
          headerResult[0].dt_req,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_cust",
          headerResult[0].cd_cust,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "nm_cust",
          headerResult[0].nm_cust,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "fg_trns",
          headerResult[0].fg_trns,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "fg_vat",
          headerResult[0].fg_vat,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_exch",
          headerResult[0].cd_exch,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "rt_exch",
          headerResult[0].rt_exch,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_dept",
          headerResult[0].cd_dept,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_emp",
          headerResult[0].cd_emp,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "nm_emp",
          headerResult[0].nm_emp,
          "noEvent"
        );
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("no_pur", "", "noEvent");
            addedNode.setDataValue(
              "no_rcv",
              result.no_io || result.no_rcv,
              "noEvent"
            );
            addedNode.setDataValue("no_line_rcv", result.no_line, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("fg_io", result.fg_io, "noEvent");
            addedNode.setDataValue("fg_trns", result.fg_trns, "noEvent");
            addedNode.setDataValue("fg_vat", result.fg_vat, "noEvent");
            addedNode.setDataValue("rt_exch", result.rt_exch, "noEvent");
            addedNode.setDataValue("cd_gp", result.cd_gp, "noEvent");
            addedNode.setDataValue("no_po", result.no_order, "noEvent");
            addedNode.setDataValue(
              "no_line_po",
              result.no_line_order || 0,
              "noEvent"
            );
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue("qt", result.qt, "noEvent");
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("am", result.am, "noEvent");
            addedNode.setDataValue("am_k", result.am_k, "noEvent");
            addedNode.setDataValue("am_vat", result.am_vat, "noEvent");
            addedNode.setDataValue("am_supply", result.am_supply, "noEvent");
            addedNode.setDataValue("um_k", result.um_k, "noEvent");
            addedNode.setDataValue("um_supply", result.um_supply, "noEvent");
            addedNode.setDataValue("dt_dlv", result.dt_dlv, "noEvent");
            addedNode.setDataValue("no_wbs", result.no_wbs, "noEvent");
            addedNode.setDataValue("no_cbs", result.no_cbs, "noEvent");
            addedNode.setDataValue("no_prd", result.no_prq, "noEvent");
            addedNode.setDataValue("cd_pjt", result.cd_pjt, "noEvent");
            addedNode.setDataValue("nm_pjt", result.nm_pjt, "noEvent");
            addedNode.setDataValue(
              "no_contract",
              result.no_contract,
              "noEvent"
            );
          }
        }
      });
      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
    if (id == "btn_tpum") {
      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      if (rowCount === 0) return;

      if (!selectedValue) {
        await showDialog("alter", "단가유형을 선택하시기 바랍니다.");
        return;
      }

      const lineUpdateList =
        gridRefs.current["grid2"].current.getChange().update;
      const lineDeleteList =
        gridRefs.current["grid2"].current.getChange().delete;
      if (lineUpdateList.length >= 1 || lineDeleteList.length >= 1) {
        await showDialog("alter", "신규가 아닌 건이 존재합니다.");
        return;
      }

      try {
        const focusedRowNodeH =
          gridRefs.current["grid1"].current.focusedRowNode();

        const selectedCdCust = focusedRowNodeH?.data?.cd_cust;
        const selectedCdExch = focusedRowNodeH?.data?.cd_exch;
        const selectedDtPur =
          focusedRowNodeH?.data?.dt_pur || dateToString(new Date());

        let displayedNodes: any[] = [];
        gridRefs.current["grid2"].current.forEachFilteredNode(
          (node: RowNode) => {
            displayedNodes.push(node);
          }
        );

        await setUmByFgUm(
          focusedRowNodeH.data,
          displayedNodes,
          "dt_pur",
          selectedValue
        );

        await CalAmBulk(
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          "rt_exch"
        );

        // for (const node of displayedNodes) {
        //   if (!node?.data) continue;
        //
        //   const cdItem = node.data.cd_item;
        //   const getUm = await set_mas_um(
        //     node,
        //     gridRefs.current["grid2"],
        //     cdItem,
        //     selectedValue,
        //     selectedCdCust,
        //     selectedCdExch,
        //     selectedDtPur,
        //     "1",
        //     "PUR",
        //     ""
        //   );
        //   const umValue = getUm[0]?.um || 0;
        //   const um_supplyValue = getUm[0]?.um_supply || 0;
        //   if (umValue === 0) continue;
        // }
        // CalAmBulk(
        //   gridRefs.current["grid1"],
        //   gridRefs.current["grid2"],
        //   "rt_exch"
        // );
      } catch (error) {}
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_pur = selectedRow.no_pur;
      setSelectedDocu(no_pur);
    } else {
      return;
    }
  };

  const onCellValueChanged1 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (colId === "rt_exch") {
      const rt_exch = Number(rowData.rt_exch ?? 1);
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (node) {
          node.setDataValue("rt_exch", rt_exch, "noEvent");
        }
      });
    }
    if (colId === "no_pur") {
      gridRefs.current["grid2"].current.applyFilters({
        no_pur: rowData.no_pur,
      });
    }
    await CalAmBulk(
      gridRefs.current["grid1"],
      gridRefs.current["grid2"],
      colId
    );
  };

  const onCellValueChanged2 = async (event: CellValueChangedEvent) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onValueChanged = async (value: string) => {
    setSelectedValue(value);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChanged1,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged2,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "매입정보" }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"입고적용"}
                  onClick={() => onGrid1BtnsClck("additem")}
                />
              </div>
            }
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_pur"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
            header={
              <div className="relative flex space-x-4">
                <SnLabel text="단가유형" type="string" />
                <div style={{ width: 180 }}>
                  <SnComboBox
                    defaultValue={"001"}
                    code="PUR001"
                    sql=""
                    isEditable={true}
                    onValueChanged={onValueChanged}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <SnButton
                    text={"단가일괄변경"}
                    onClick={() => {
                      onGrid2BtnsClick("btn_tpum");
                    }}
                  />
                </div>
              </div>
            }
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_PUR_PTX_REG_001;
